import { formatNumber } from 'dashboard/lib/utils/formatters';
import { stringifyLatitude, stringifyLongitude } from 'dashboard/lib/utils/stringFormat';
import { InquirySession } from 'dashboard/models/inquiry-session';
import { ListItemGeolocation, ListItemStatus } from 'dashboard/models/list-item';
import { ReportProfile } from 'dashboard/models/report/profile';
import {
  VerificationDatabase,
  VerificationGovernmentId,
  VerificationPhoneNumber,
} from 'dashboard/models/verification';
import { InquiryDeviceSessionSummaryItem } from 'dashboard/views/DashboardInquiriesShowView/InquiryDeviceSessionSummary';
import { ApiModel } from 'lib/interfaces/api';
import { Place } from 'lib/interfaces/place';

export enum MapMarkerStyleType {
  Default = 'default',
  Secondary = 'secondary',
  CountBased = 'count-based',
  CircleOnly = 'circle-only',
}

type GeocodedEntity =
  | ApiModel.VerificationPhoneNumber
  | ApiModel.VerificationGovernmentId
  | ApiModel.VerificationDatabase
  | ApiModel.InquirySession
  | ApiModel.ReportProfile;

const GEOCODED_ENTITY_TYPE_TO_LOCATION_LABEL: Record<GeocodedEntity, string> = {
  [ApiModel.VerificationPhoneNumber]: 'Residence (Phone)',
  // TODO: use old models for now until backfill except passport
  [ApiModel.VerificationGovernmentId]: 'Residence (Government ID)',
  [ApiModel.VerificationDatabase]: 'Residence (Database)',
  [ApiModel.InquirySession]: 'Device (approximate)',
  [ApiModel.ReportProfile]: 'Residence (Report)',
};

export type MapMarkerLocationSource = 'GPS' | 'IP';

export const MAP_MARKER_SOURCE_LABEL: Record<MapMarkerLocationSource, string> = {
  GPS: 'GPS',
  IP: 'IP Lookup',
};

/**
 * Interface to encapsulate any information the marker needs
 * for both custom styling and display on Google Maps
 */

export class MapMarker implements Place {
  lat: number;
  lng: number;

  radiusMeters?: number;
  /**
   * Shows a red dot in the top right of the marker if this is true
   */
  hasDot?: boolean;
  /**
   * Palettes for marker styling other than the color of the marker
   * in active/default states (e.g. color and opacity of its circle)
   */
  styleType?: MapMarkerStyleType;
  /**
   * Strings to display in the tooltip
   */
  primaryTooltipLabel?: string;
  secondaryTooltipLabel?: string;
  /**
   * Color of the marker when it is focused (e.g. hovered on)
   */
  focusedMarkerColor?: string;
  defaultMarkerColor?: string;
  overlapCount?: number;

  constructor({
    lat,
    lng,
    radiusMeters,
    hasDot,
    styleType,
    primaryTooltipLabel,
    secondaryTooltipLabel,
    focusedMarkerColor,
    defaultMarkerColor,
    overlapCount,
  }: MapMarker) {
    this.lat = lat;
    this.lng = lng;
    this.radiusMeters = radiusMeters;
    this.hasDot = hasDot;
    this.styleType = styleType;
    this.primaryTooltipLabel = primaryTooltipLabel;
    this.secondaryTooltipLabel = secondaryTooltipLabel;
    this.focusedMarkerColor = focusedMarkerColor;
    this.defaultMarkerColor = defaultMarkerColor;
    this.overlapCount = overlapCount;
  }

  static fromBasicGeocodedEntity(
    entity:
      | VerificationPhoneNumber
      | VerificationGovernmentId
      | VerificationDatabase
      | ReportProfile,
  ): MapMarker {
    // We currently cannot create list matches on Reports since they run asynchronously
    const listMatchCount =
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      entity.type !== ApiModel.ReportProfile ? entity.relationships['list-matches'].data.length : 0;

    return new MapMarker({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      lat: entity.attributes.latitude,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      lng: entity.attributes.longitude,
      primaryTooltipLabel: GEOCODED_ENTITY_TYPE_TO_LOCATION_LABEL[entity.type],
      secondaryTooltipLabel: listMatchCount > 0 ? `${listMatchCount} matches` : undefined,
    });
  }

  static fromInquirySession(session: InquirySession, config?: { useGps: boolean }): MapMarker {
    const {
      latitude,
      longitude,
      'gps-latitude': gpsLatitude,
      'gps-longitude': gpsLongitude,
      'device-type': deviceType,
      'os-full-version': osFullVersion,
      'os-name': osName,
    } = session.attributes;

    const { 'list-matches': listMatches } = session.relationships;
    const listMatchCount = listMatches.data.length;
    const os = [osName, osFullVersion].filter(Boolean).join(' ') || 'Unknown OS';
    const deviceInfo = `Device (${os}, ${deviceType || 'Unknown device'})`;

    const [lat, lng, locationSourceLabel] = config?.useGps
      ? [gpsLatitude, gpsLongitude, `\nfrom ${MAP_MARKER_SOURCE_LABEL['GPS']}`]
      : [latitude, longitude, `\nfrom ${MAP_MARKER_SOURCE_LABEL['IP']}`];

    return new MapMarker({
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      lat,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      lng,
      primaryTooltipLabel: `${deviceInfo} ${locationSourceLabel}`,
      secondaryTooltipLabel: listMatchCount > 0 ? `${listMatchCount} matches` : undefined,
    });
  }

  static fromListItemGeolocation(listItem: ListItemGeolocation) {
    const {
      latitude,
      longitude,
      status,
      'radius-meters': radiusMeters,
      'match-count': matchCount,
    } = listItem.attributes;

    // List item is likely redacted
    if (latitude == null || longitude == null || radiusMeters == null) {
      return null;
    }

    const locationLabel = `${stringifyLatitude(latitude)}, ${stringifyLongitude(
      longitude,
    )} (${formatNumber(radiusMeters)}m)`;

    return new MapMarker({
      primaryTooltipLabel: locationLabel,
      secondaryTooltipLabel: `${matchCount} matches`,
      styleType:
        status === ListItemStatus.Archived
          ? MapMarkerStyleType.Secondary
          : MapMarkerStyleType.Default,
      lat: latitude,
      lng: longitude,
      radiusMeters: radiusMeters,
    });
  }

  static fromInquiryDeviceSessionSummaryItem(
    item: InquiryDeviceSessionSummaryItem,
    config?: { useGps: boolean },
  ) {
    const {
      deviceDetails,
      latitude,
      longitude,
      gpsLatitude,
      gpsLongitude,
      formattedLatLng,
      formattedGpsLatLng,
      sessionName,
    } = item;
    const [lat, lng, formattedLocationLabel] = config?.useGps
      ? [gpsLatitude, gpsLongitude, formattedGpsLatLng]
      : [latitude, longitude, formattedLatLng];
    if (lat == null || lng == null) {
      return null;
    }
    const locationSource: MapMarkerLocationSource = config?.useGps ? 'GPS' : 'IP';

    const secondaryTooltipLabel = `${sessionName}: ${deviceDetails} \nfrom ${MAP_MARKER_SOURCE_LABEL[locationSource]}`;

    return new MapMarker({
      lat,
      lng,
      primaryTooltipLabel: formattedLocationLabel ?? undefined,
      secondaryTooltipLabel: secondaryTooltipLabel,
      styleType: MapMarkerStyleType.Default,
      overlapCount: 1,
    });
  }

  static createUpdatedMarkerTooltip(tooltip: string, newMarkerType: MapMarkerLocationSource) {
    if (
      tooltip.includes(MAP_MARKER_SOURCE_LABEL['IP']) &&
      tooltip.includes(MAP_MARKER_SOURCE_LABEL['GPS'])
    ) {
      return tooltip;
    }
    const combinedSourceTooltipTag = 'IP Lookup + GPS';
    if (tooltip.includes(MAP_MARKER_SOURCE_LABEL['IP']) && newMarkerType === 'GPS') {
      return tooltip.replace(MAP_MARKER_SOURCE_LABEL['IP'], combinedSourceTooltipTag);
    }
    if (tooltip.includes(MAP_MARKER_SOURCE_LABEL['GPS']) && newMarkerType === 'IP') {
      return tooltip.replace(MAP_MARKER_SOURCE_LABEL['GPS'], combinedSourceTooltipTag);
    }
    return tooltip;
  }

  static extractLocationSourceTypeFromTooltip(
    tooltip: string,
  ): MapMarkerLocationSource | undefined {
    if (tooltip.includes(MAP_MARKER_SOURCE_LABEL['IP'])) {
      return 'IP';
    }
    if (tooltip.includes(MAP_MARKER_SOURCE_LABEL['GPS'])) {
      return 'GPS';
    }
    return;
  }
}
